import { useGetInvoicePdfUrlLazyQuery, useGetOrderTransactionsQuery } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { CreditCard, FileText, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { SectionCard } from '../ui/section-card';
import { StatusBadge } from '../ui/status-badge';
import { FilePreview, type FilePreviewItem } from '../ui/file-preview';
import { CurrencyText } from '../CurrencyText';
import { formatDate } from '../../lib/utils';

/* ── Status tone mapping ── */

const STATUS_TONE: Record<string, 'success' | 'warn' | 'info' | 'neutral' | 'danger'> = {
	paid: 'success',
	succeeded: 'success',
	completed: 'success',
	invoiced: 'info',
	processing: 'info',
	pending: 'warn',
	unpaid: 'warn',
	failed: 'danger',
	cancelled: 'neutral',
	refunded: 'neutral',
	waived: 'neutral',
};

function getStatusTone(status: string): 'success' | 'warn' | 'info' | 'neutral' | 'danger' {
	return STATUS_TONE[status.toLowerCase()] ?? 'neutral';
}

interface OrderTransactionsProps {
	orderId: string;
	skip?: boolean;
	/** Bump this to force a refetch (e.g. after a payment completes). */
	reloadKey?: number;
}

export function OrderTransactions({
	orderId,
	skip = false,
	reloadKey = 0,
}: OrderTransactionsProps) {
	const { data, loading, refetch } = useGetOrderTransactionsQuery({
		variables: { orderId },
		fetchPolicy: 'no-cache',
		skip,
	});

	// After a payment completes, poll briefly: the transaction is booked via a
	// Stripe webhook, so it can lag a second or two behind checkout completion.
	useEffect(() => {
		if (reloadKey === 0 || skip) return;
		refetch();
		const timers = [2500, 6000].map((ms) => setTimeout(() => refetch(), ms));
		return () => timers.forEach(clearTimeout);
	}, [reloadKey, skip, refetch]);

	const [fetchPdfUrl] = useGetInvoicePdfUrlLazyQuery();
	const [loadingInvoice, setLoadingInvoice] = useState<number | null>(null);
	const [invoicePreview, setInvoicePreview] = useState<FilePreviewItem | null>(null);

	const viewInvoice = async (invoiceNumber: number) => {
		setLoadingInvoice(invoiceNumber);
		try {
			const { data: pdf } = await fetchPdfUrl({
				variables: { invoiceNumber },
				fetchPolicy: 'no-cache',
			});
			const url = pdf?.getInvoicePdfUrl;
			if (url) {
				setInvoicePreview({
					id: `invoice-${invoiceNumber}`,
					url,
					name: `Invoice #${invoiceNumber}.pdf`,
					mimeType: 'application/pdf',
				});
			} else {
				toast.error('Invoice PDF is not available yet');
			}
		} catch {
			toast.error('Failed to open invoice');
		} finally {
			setLoadingInvoice(null);
		}
	};

	const transactions = data?.getOrderTransactionsV2 ?? [];
	const visible = transactions.filter((t) => !t.isHistory);

	if (loading) {
		return (
			<SectionCard title="Transactions">
				<div className="flex flex-col gap-2 px-4 py-4">
					{Array.from({ length: 3 }).map((_, i) => (
						<div key={i} className="h-14 animate-pulse rounded-lg bg-surface-fill" />
					))}
				</div>
			</SectionCard>
		);
	}

	return (
		<>
			<SectionCard title={`Transactions (${visible.length})`}>
				{visible.length === 0 ? (
					<div className="flex flex-col items-center gap-3 py-12 text-center">
						<div className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
							<Receipt size={20} />
						</div>
						<div className="text-base text-text-2">No transactions yet</div>
						<div className="text-sm text-text-4">
							Transactions will appear here after payments are made.
						</div>
					</div>
				) : (
					<div>
						{visible.map((txn, i) => {
							const tone = getStatusTone(txn.status);
							const statusLabel =
								txn.status.charAt(0).toUpperCase() + txn.status.slice(1);
							const isStripe = txn.method === 'Stripe';

							return (
								<div
									key={txn.key}
									className="flex items-start gap-3 px-4 py-3.5"
									style={{
										borderBottom:
											i < visible.length - 1
												? '0.5px solid var(--line-1)'
												: 'none',
									}}
								>
									<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill">
										{isStripe ? (
											<CreditCard size={14} className="text-text-4" />
										) : (
											<FileText size={14} className="text-text-4" />
										)}
									</div>
									<div className="min-w-0 flex-1">
										<div className="mb-0.5 flex items-center gap-2">
											<span className="text-sm font-medium capitalize text-text-1">
												{txn.kind} payment
											</span>
											<StatusBadge
												active
												activeLabel={statusLabel}
												activeTone={tone}
											/>
										</div>
										<div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-4">
											{txn.method && <span>{txn.method}</span>}
											{txn.invoiceNumber != null && (
												<button
													type="button"
													onClick={() => viewInvoice(txn.invoiceNumber!)}
													disabled={loadingInvoice === txn.invoiceNumber}
													className="inline-flex items-center gap-1 text-text-3 underline-offset-2 transition-colors hover:text-text-1 hover:underline disabled:opacity-60"
												>
													Invoice #{txn.invoiceNumber}
													<FileText size={10} />
												</button>
											)}
											<span>{formatDate(txn.date)}</span>
										</div>
										{txn.descriptions.length > 0 && (
											<div className="mt-1.5 flex flex-col gap-0.5 text-xs text-text-3">
												{txn.descriptions.map((desc, j) => (
													<span key={j}>
														{desc.sampleRound != null &&
															`Sample round ${desc.sampleRound}: `}
														{desc.text}
													</span>
												))}
											</div>
										)}
									</div>
									<CurrencyText
										amount={txn.totalPrice}
										currency={txn.currency}
										className="shrink-0 text-sm font-medium text-text-1"
									/>
								</div>
							);
						})}
					</div>
				)}
			</SectionCard>
			{invoicePreview && (
				<FilePreview files={[invoicePreview]} onClose={() => setInvoicePreview(null)} />
			)}
		</>
	);
}
